import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature, useViewer } from '@treams/treams-hooks';
import { ArchiveIcon, ArrowLeftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Callout, Container, Toolbar } from '@treams/treams-ui';

import { ContextSidebar, ContextSidebarLayout } from '../context-sidebar';
import { ModalContext } from '../navigation/ModalNavigation';
import ArchiveRestoreButton from './ArchiveRestoreButton';
import { NotesDetailContainerWithoutSidebar } from './NotesDetailContainer';

export default function Note(props: { id: string; userID?: string }) {
	const viewer = useViewer();
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);

	const note = graphql.useNoteQuery({ variables: { id: props.id } });

	// Get the subject of the note (the person the note is about)
	const subjectID = note.data?.note.subject?.id;

	// Fetch full user info for the subject (needed for permissions)
	const { data: subjectData } = graphql.useUserQuery({
		variables: { id: subjectID! },
		skip: !subjectID,
	});

	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const onSidebarOpen = useCallback((open: boolean) => setIsSidebarOpen(open), []);

	return (
		<ModalContext.Consumer>
			{({ isModal }) =>
				!isModal && hasOneOnOnes && subjectData?.user ? (
					<ContextSidebarLayout
						isSideBarOpen={isSidebarOpen}
						onSidebarOpen={onSidebarOpen}
						renderSidebar={({ onClose }) => (
							<ContextSidebar
								onClose={onClose}
								user={subjectData.user as graphql.User}
								otherUserID={viewer?.id}
							/>
						)}
					>
						<NoteContent id={props.id} note={note.data?.note} userID={props.userID} />
					</ContextSidebarLayout>
				) : (
					<NoteContent id={props.id} note={note.data?.note} userID={props.userID} />
				)
			}
		</ModalContext.Consumer>
	);
}

function NoteContent(props: {
	id: string;
	note: graphql.NoteFragment | undefined;
	userID?: string;
}) {
	const { t } = useTranslation();
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);
	const [wasArchived, setWasArchived] = useState<boolean | undefined>(props.note?.isArchived);

	useEffect(() => {
		if (wasArchived === undefined && props.note?.isArchived !== undefined) {
			setWasArchived(props.note.isArchived);
		}
	}, [props.note?.isArchived, wasArchived]);

	const showArchiveRestoreButton = useMemo(() => {
		if (wasArchived) {
			return props.note?.viewerCanRestore;
		}

		return props.note?.viewerCanArchive;
	}, [wasArchived, props.note]);

	return (
		<ModalContext.Consumer>
			{({ isModal }) => (
				<Box flexGrow={1} flexShrink={1} mt={4}>
					<Container>
						{wasArchived && (
							<Callout
								mb={3}
								color="red"
								icon={<ArchiveIcon />}
								title={t('note.show.archived.title')}
								description={t('note.show.archived.description')}
							/>
						)}
						<Toolbar>
							{!isModal && (
								<BackButton
									userID={props.userID}
									subject={props.note?.subject}
									wasArchived={wasArchived}
								/>
							)}
							<Toolbar.Spacing />
							{props.note && showArchiveRestoreButton && !hasOneOnOnes && (
								<ArchiveRestoreButton note={props.note} />
							)}
						</Toolbar>
						<NotesDetailContainerWithoutSidebar id={props.id} />
						{props.note && showArchiveRestoreButton && hasOneOnOnes && (
							<Flex mt={3} mb={5}>
								<ArchiveRestoreButton note={props.note} small />
							</Flex>
						)}
					</Container>
				</Box>
			)}
		</ModalContext.Consumer>
	);
}

function BackButton(props: {
	userID?: string;
	subject: graphql.NoteFragment['subject'];
	wasArchived?: boolean;
}) {
	const { t } = useTranslation();
	const viewer = useViewer();
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);

	const backLink = useMemo(() => {
		if (props.userID) {
			return `/users/${props.userID}/notes`;
		}

		if (props.wasArchived) {
			return '/notes/archive';
		}

		if (hasOneOnOnes) {
			if (props.subject) {
				if (props.subject.id === viewer?.id) {
					return `/notes/me`;
				}

				return `/notes/subject/${props.subject.id}`;
			}

			return '/notes/drafts';
		}

		return '/notes';
	}, [props.userID, hasOneOnOnes, props.subject, viewer?.id, props.wasArchived]);

	const backTarget = useMemo(() => {
		if (props.subject && props.subject.id !== viewer?.id && !props.wasArchived) {
			return props.subject.firstName;
		}

		return undefined;
	}, [props.subject, viewer?.id, props.wasArchived]);

	return (
		<Button
			icon={<ArrowLeftIcon />}
			link={backLink}
			title={backTarget ? t('generic.backTo', { to: backTarget }) : t('generic.backToOverview')}
			type="plain"
			ml="-16px"
		/>
	);
}
